import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import apiClient from '../api/client';
import useStoreInfo from '../hooks/useStoreInfo';

const ContactPage = () => {
  const contact = useStoreInfo();   // e-mail, phone, address from the admin (Settings → General)
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post('/contact', {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
      });
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      // Fallback to mock success behaviour on API failure
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Contact Us — Skinn Thrive</title></Helmet>
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Contact Us</h1>
          <p className="text-gray-500">We're here to help. Reach out and we'll respond within 24 hours.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Form */}
          <div className="card p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {[
                { key: 'name', label: 'Your Name', type: 'text', placeholder: 'John Doe' },
                { key: 'email', label: 'Email Address', type: 'email', placeholder: 'john@example.com' },
                { key: 'phone', label: 'Phone Number', type: 'tel', placeholder: '+91 8320206804' },
                { key: 'subject', label: 'Subject', type: 'text', placeholder: 'Order issue, product query...' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                  <input type={f.type} value={form[f.key]} onChange={e => setForm(d => ({ ...d, [f.key]: e.target.value }))} placeholder={f.placeholder} className="input-field" required />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea value={form.message} onChange={e => setForm(d => ({ ...d, message: e.target.value }))} rows={5} placeholder="Describe your query in detail..." className="input-field resize-none" required />
              </div>
              <button type="submit" disabled={loading} className="w-full btn-primary py-3.5">{loading ? 'Sending...' : 'Send Message'}</button>
            </form>
          </div>

          {/* Info */}
          <div className="space-y-6">
            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/918320206804?text=Hi!%20I%20have%20a%20question%20about%20Skinn%20Thrive."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 rounded-xl transition-transform hover:scale-[1.02] active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
            >
              <span className="shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-10 h-10" fill="white">
                  <path d="M16.002 2.667C8.636 2.667 2.667 8.636 2.667 16c0 2.363.638 4.67 1.848 6.688L2.667 29.333l6.813-1.786A13.288 13.288 0 0016.002 29.333c7.364 0 13.331-5.969 13.331-13.333S23.366 2.667 16.002 2.667zm0 24.267a11 11 0 01-5.617-1.544l-.403-.24-4.043 1.06 1.08-3.94-.262-.417A11.002 11.002 0 015.002 16c0-6.065 4.935-11 11-11s11 4.935 11 11-4.935 11-11 11zm6.03-8.233c-.33-.165-1.952-.963-2.255-1.073-.303-.11-.523-.165-.743.165-.22.33-.852 1.073-1.044 1.293-.193.22-.385.248-.715.083-.33-.165-1.394-.514-2.655-1.638-.98-.875-1.643-1.956-1.835-2.286-.193-.33-.02-.508.145-.672.149-.148.33-.385.495-.578.165-.193.22-.33.33-.55.11-.22.055-.413-.028-.578-.083-.165-.743-1.79-1.018-2.452-.268-.645-.54-.557-.743-.567l-.633-.012a1.213 1.213 0 00-.88.413c-.303.33-1.155 1.128-1.155 2.75s1.183 3.19 1.348 3.41c.165.22 2.328 3.556 5.643 4.988.788.34 1.404.543 1.883.695.792.252 1.513.216 2.083.131.635-.095 1.952-.798 2.228-1.569.275-.77.275-1.43.193-1.568-.083-.138-.303-.22-.633-.385z" />
                </svg>
              </span>
              <div>
                <p className="font-bold text-white text-base">Chat on WhatsApp</p>
                <p className="text-green-100 text-sm mt-0.5">+91 83202 06804</p>
                <p className="text-green-200 text-xs mt-0.5">Fastest response · Mon–Sat, 9 AM – 7 PM</p>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white ml-auto shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>

            {[
              { icon: '📧', title: 'Email Support', detail: contact.email, sub: 'We reply within 24 hours' },
              { icon: '📞', title: 'Phone Support', detail: contact.phone, sub: 'Mon–Sat, 9 AM – 7 PM IST' },
              // the address saved in the admin, else the office address shown before
              contact.address
                ? { icon: '📍', title: 'Office Address', detail: contact.address, sub: '' }
                : { icon: '📍', title: 'Office Address', detail: 'G-09 Prime Vivane, L Narayana Reddy Main Rd, Kondappa Layout, Vignan Nagar', sub: 'Doddanekkundi, Bengaluru, Karnataka – 560037' },
            ].map(c => (
              <div key={c.title} className="flex gap-4 p-5 bg-secondary rounded-xl">
                <span className="text-2xl shrink-0">{c.icon}</span>
                <div>
                  <p className="font-semibold text-gray-800">{c.title}</p>
                  <p className="text-gray-700 mt-0.5">{c.detail}</p>
                  {c.sub && <p className="text-xs text-gray-400 mt-0.5">{c.sub}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
